import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { withTranslation, WithTranslation } from 'react-i18next';

import { useAssistant } from '@core/assistant/providers/Provider';
import { enableDebugControls } from '@core/assistant/constants';
import { SCREENS } from '@core/assistant/interfaces';
import { useDebug } from '@core/assistant/providers/Debug';
import { useMicRecord } from '@core/assistant/providers/Record';
import { useGreetings } from '@core/assistant/providers/Greetings';
import { log } from '@core/assistant/providers/Debug';

import DebugOverlay from './DebugOverlay';
import QuizScreen from '../QuizScreen/QuizScreen';
import { useStyles } from './styles';

const DefaultScreen = ({ t: translate }: WithTranslation) => {
  const { generateGreetingsAudio } = useGreetings();
  const classes = useStyles();

  const [showDebugUI, setShowDebugUI] = useState(false);
  const [showScriptQuiz, setShowScriptQuiz] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showNextButton, setShowNextButtton] = useState(false);
  const intervalTimer = useRef<NodeJS.Timer | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const {
    navigate,
    setOpen,
    setNavigationHistory,
    stopPlayback,
    isRecording,
    setIsRecording,
    audioPromptHistory,
    teleportToRegion,
    startFlow,
    linearScriptPlayerRef,
    isAudioPlaybackActive,
    fetchScripts,
    fetchParticipantProgress,
    resetProgress,
    saveQuizAnswers,
  } = useAssistant();

  const {
    queryBedrockTest,
    generateAudioPlaybackTest,
    testKeyboard,
    sendWSMessage,
  } = useDebug();

  const { startRecording, stopRecording } = useMicRecord();

  const handleRecording = () => {
    log('start/stop recording button clicked');

    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }

    setIsRecording(!isRecording);
  };

  const handleTestKeystroke = () => {
    testKeyboard();
  };

  const handleTestTeleport = () => {
    teleportToRegion('auditorium');
  };

  const handleSendWS = () => {
    log('handleSendWS', inputValue);
    sendWSMessage(inputValue);
  };

  const handleNext = () => {
    setShowNextButtton(false);
    linearScriptPlayerRef.current?.triggerNext();
  };

  const handleScriptQuizConfirm = (selectedOptions: number[]) => {
    saveQuizAnswers(selectedOptions);
    setShowScriptQuiz(false);
    linearScriptPlayerRef.current?.triggerNext();
  };

  useEffect(() => {
    log('audioPromptHistory', audioPromptHistory);
    log(
      'new item in audioPromptHistory',
      audioPromptHistory[audioPromptHistory.length - 1],
    );
  }, [audioPromptHistory]);

  useEffect(() => {
    console.warn('isAudioPlaybackActive', isAudioPlaybackActive);

    const linearScriptPlayerState =
      linearScriptPlayerRef.current?.getCurrentState();
    console.warn('linearScriptPlayerState', linearScriptPlayerState);

    const currentNode = linearScriptPlayerState?.currentNode;

    if (currentNode?.questionary?.type === 'multiple_choice') {
      setShowScriptQuiz(true);
      setShowNextButtton(false);
    } else if (!isAudioPlaybackActive && currentNode?.awaitButtonNext) {
      setShowNextButtton(true);
    } else if (!currentNode?.awaitButtonNext) {
      setShowNextButtton(false);
    } else if (linearScriptPlayerState?.isCompleted) {
      setShowNextButtton(false);
    }
  }, [isAudioPlaybackActive]);

  // dirty fix to disable Next button when script is completed
  useEffect(() => {
    intervalTimer.current = setInterval(() => {
      const linearScriptPlayerState =
        linearScriptPlayerRef.current?.getCurrentState();

      if (!linearScriptPlayerState?.isRunning) {
        setShowNextButtton(false);
      }
    }, 1000);
  }, []);

  const scriptPlayerState = linearScriptPlayerRef.current?.getCurrentState();
  const questionaryInfo = linearScriptPlayerRef.current?.getQuestionaryInfo();
  const maxOptions = linearScriptPlayerRef.current?.getMaxOptionsCount() ?? 0;
  const activeQuestionary = scriptPlayerState?.currentNode?.questionary;
  const activeQuestionText = scriptPlayerState?.currentNode?.text;

  return (
    <Box sx={classes.box}>
      <Button sx={classes.button} onClick={() => navigate(SCREENS.HOW_TO)}>
        {translate('assistant.howTo.title')}
      </Button>
      <Button
        sx={{ ...classes.button, ...classes.blueButton }}
        onClick={() => navigate(SCREENS.CHAT)}
      >
        {translate('assistant.askMe.title')}
      </Button>
      <Button
        sx={classes.button}
        onClick={() => handleNext()}
        disabled={!showNextButton}
      >
        Next
      </Button>
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={showDebugUI}
              onChange={() => setShowDebugUI(!showDebugUI)}
            />
          }
          sx={{
            color: '#fff',
          }}
          label="Show debug"
        />
      </Box>
      <Button
        sx={classes.hideButton}
        onClick={() => {
          stopPlayback();
          setOpen(false);
          setNavigationHistory([]);
          navigate(SCREENS.DEFAULT);
        }}
      >
        {translate('assistant.hideNoa')}
      </Button>

      {showScriptQuiz && activeQuestionary && activeQuestionText && (
        <QuizScreen
          questionText={activeQuestionText}
          questionary={activeQuestionary}
          questionNumber={questionaryInfo?.current}
          totalQuestions={questionaryInfo?.total}
          maxOptions={maxOptions}
          onConfirm={handleScriptQuizConfirm}
        />
      )}

      {showDebugUI && (
        <DebugOverlay
          startFlow={(flowId) => {
            setShowDebugUI(false);
            startFlow(flowId);
          }}
          stopPlayer={() => {
            linearScriptPlayerRef.current?.stop();
            setShowScriptQuiz(false);
          }}
          generateGreetingsAudio={generateGreetingsAudio}
          fetchScripts={fetchScripts}
          fetchParticipantProgress={fetchParticipantProgress}
          resetProgress={resetProgress}
        />
      )}
    </Box>
  );
};

export default withTranslation('common')(DefaultScreen);
